import { ActivatedRoute } from '@angular/router';
import { myDigitalHomeBusinessRoute } from '../member-profile/my-digital-home-nav.util';
import { isPetClinicStoreType } from '../../shared/constants/clinic-store-types.util';
import {
  petAddCustomerRoute,
  petAppointmentsRoute,
  petBusinessBase,
  petPatientRoute
} from '../pet-business-dashboard/pet-route.util';

/** Resolve `storeCode` from nested clinic shell routes. */
export function clinicStoreCodeFromRoute(route: ActivatedRoute): string {
  let r: ActivatedRoute | null = route;
  while (r) {
    const code = (r.snapshot.paramMap.get('storeCode') || '').trim();
    if (code) return code;
    r = r.parent;
  }
  return '';
}

export function clinicBusinessBase(storeCode: string): string[] {
  const code = String(storeCode || '').trim();
  return code ? ['/mybusiness-clinic', code] : myDigitalHomeBusinessRoute();
}

export function clinicBusinessPatientsRoute(storeCode: string): string[] {
  return [...clinicBusinessBase(storeCode), 'patients'];
}

export function clinicBusinessAccountRoute(storeCode: string): string[] {
  return [...clinicBusinessBase(storeCode), 'account'];
}

export function clinicPatientRoute(
  storeCode: string,
  patientId: number | string
): string[] {
  return [...clinicBusinessBase(storeCode), 'patient', String(patientId)];
}

/**
 * Patient / pet-owner detail route — pet stores use `/mybusiness-pet`, others clinic.
 */
export function storePatientRoute(
  storeCode: string,
  patientId: number | string,
  storeType?: string | null
): string[] {
  if (isPetClinicStoreType(storeType)) {
    return petPatientRoute(storeCode, patientId);
  }
  return clinicPatientRoute(storeCode, patientId);
}

export function storePatientsListRoute(
  storeCode: string,
  storeType?: string | null
): string[] {
  if (isPetClinicStoreType(storeType)) {
    // Pet shell lists owners under Customers (not clinic patients list).
    return [...petBusinessBase(storeCode), 'customers'];
  }
  return clinicBusinessPatientsRoute(storeCode);
}

export function storeAddCustomerRoute(
  storeCode: string,
  storeType?: string | null
): string[] {
  if (isPetClinicStoreType(storeType)) {
    return petAddCustomerRoute(storeCode);
  }
  return clinicAddCustomerRoute(storeCode);
}

export function storeAppointmentsRoute(
  storeCode: string,
  storeType?: string | null
): string[] {
  if (isPetClinicStoreType(storeType)) {
    return petAppointmentsRoute(storeCode);
  }
  return clinicAppointmentsRoute(storeCode);
}

export function storeBusinessHomeRoute(
  storeCode: string,
  storeType?: string | null
): string[] {
  if (isPetClinicStoreType(storeType)) {
    return petBusinessBase(storeCode);
  }
  return clinicBusinessBase(storeCode);
}

export function clinicAppointmentsRoute(storeCode: string): string[] {
  return [...clinicBusinessBase(storeCode), 'appointments'];
}

export function clinicAddCustomerRoute(storeCode: string): string[] {
  return [...clinicBusinessBase(storeCode), 'addcustomer'];
}

export function clinicProductsRoute(storeCode: string): string[] {
  return [...clinicBusinessBase(storeCode), 'products'];
}

export function clinicServicesRoute(storeCode: string): string[] {
  return [...clinicBusinessBase(storeCode), 'services'];
}

export function clinicTeamRoute(storeCode: string): string[] {
  return [...clinicBusinessBase(storeCode), 'team'];
}

export function clinicAttendanceRoute(storeCode: string): string[] {
  return [...clinicBusinessBase(storeCode), 'attendance'];
}

export function clinicReportsRoute(storeCode: string): string[] {
  return [...clinicBusinessBase(storeCode), 'reports'];
}

export function clinicLeadsReportRoute(storeCode: string): string[] {
  return [...clinicBusinessBase(storeCode), 'admin-leads-report'];
}

export function clinicHrAdminRoute(storeCode: string): string[] {
  return [...clinicBusinessBase(storeCode), 'admin-hr'];
}

export function clinicEnquiriesRoute(storeCode: string): string[] {
  return [...clinicBusinessBase(storeCode), 'enquiries'];
}

export function clinicBlogsRoute(storeCode: string): string[] {
  return [...clinicBusinessBase(storeCode), 'blogs'];
}

export function clinicBillingRoute(storeCode: string, patientId?: number | string): string[] {
  const base = [...clinicBusinessBase(storeCode), 'billing'];
  if (patientId != null && String(patientId).trim()) {
    return [...base, String(patientId)];
  }
  return base;
}

/** Vruksha invoices list nested under clinic shell. */
export function clinicInvoicesRoute(storeCode: string): string[] {
  const code = String(storeCode || '').trim();
  return code ? ['/mybusiness-clinic', code, 'vruksha', 'invoices'] : ['/digitalbusiness'];
}

/** Vruksha invoice create (optional clinic patient preselect via query). */
export function clinicInvoiceCreateRoute(
  storeCode: string,
  clinicCustomerId?: number | string | null
): { commands: string[]; queryParams?: Record<string, string> } {
  const code = String(storeCode || '').trim();
  const commands = code
    ? ['/mybusiness-clinic', code, 'vruksha', 'invoice', 'create']
    : ['/vruksha', 'invoice', 'create'];
  const id = clinicCustomerId != null ? String(clinicCustomerId).trim() : '';
  if (id) {
    return { commands, queryParams: { clinicCustomerId: id } };
  }
  return { commands };
}

/**
 * Pet stores bill only via Vruksha. Other clinics use the in-dashboard billing page.
 */
export function clinicPatientBillNavRoute(
  storeCode: string,
  storeType?: string | null,
  patientId?: number | string | null
): { commands: string[]; queryParams?: Record<string, string> } {
  if (isPetClinicStoreType(storeType)) {
    if (patientId != null && String(patientId).trim()) {
      return clinicInvoiceCreateRoute(storeCode, patientId);
    }
    return { commands: clinicInvoicesRoute(storeCode) };
  }
  return { commands: clinicBillingRoute(storeCode, patientId ?? undefined) };
}
